import { useEffect, useState, useCallback } from "react";
import { api, getProductionId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, LogOut } from "lucide-react";
import FormFill from "@/pages/FormFill";

/**
 * Hard-block gate. Whenever the signed-in user has one or more required,
 * active forms they haven't submitted yet, we mount a full-screen modal
 * that they must complete (one at a time) before they can use anything
 * else. Only escape hatch is the logout button.
 *
 * Not shown for super_admin/producer since they don't have production
 * memberships that assign forms. Impersonated sessions DO see the gate,
 * because that's the whole point — you should see exactly what they see.
 */
export default function PendingFormsGate() {
  const { user, logout } = useAuth();
  const productionId = getProductionId();
  const [pending, setPending] = useState([]);
  const [checked, setChecked] = useState(false);

  const check = useCallback(async () => {
    if (!user?.id || !productionId) { setPending([]); setChecked(true); return; }
    // Bail for real super_admins AND anyone super_admin is impersonating —
    // impersonation is a support tool, not a signing session, so we never
    // want to hard-block or coerce the super into completing someone
    // else's compliance paperwork.
    if (user.role === "super_admin" || user.impersonated_by) {
      setPending([]); setChecked(true); return;
    }
    try {
      const { data } = await api.get("/forms/pending", { params: { production_id: productionId } });
      setPending(Array.isArray(data) ? data : []);
    } catch {
      setPending([]);
    } finally { setChecked(true); }
  }, [user?.id, user?.role, user?.impersonated_by, productionId]);

  useEffect(() => { check(); }, [check]);

  if (!checked || pending.length === 0) return null;
  const form = pending[0];

  const handleCompleted = async () => {
    // Refetch; if more pending, the next one replaces this one in-place.
    await check();
  };

  return (
    <div
      data-testid="pending-forms-gate"
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
    >
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-amber-400/30 rounded-lg shadow-2xl">
        <header className="border-b border-white/10 p-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 inline-flex items-center gap-1.5">
              <ShieldAlert className="w-3 h-3" /> Action required
              {pending.length > 1 && (
                <span className="text-zinc-500 ml-2">Form 1 of {pending.length}</span>
              )}
            </div>
            <h2 className="mt-1 font-display text-2xl tracking-tight">{form.title}</h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-xl">
              You need to complete and sign this before you can access anything else.
            </p>
          </div>
          <button
            data-testid="gate-logout-btn"
            onClick={logout}
            className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1.5 h-9 px-3 rounded border border-white/10"
            title="Sign out instead"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </header>
        <div className="p-5 md:p-6 max-h-[70vh] overflow-y-auto">
          <FormFill formIdOverride={form.id} onCompleted={handleCompleted} embedded />
        </div>
      </div>
    </div>
  );
}
